import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { GameId, SkillGameId, SlotId } from '@tg/shared';
import LobbyPage from '@/features/lobby/LobbyPage';

/**
 * Route registry: every page under /:lang/<path>.
 * A game is added with ONE line — replace its stub with a lazy import of the page module
 * (default export, wrapped in <GameShell> by the page itself):
 *
 *   crash: page(() => import('@/games/crash/CrashPage')),
 *   garden: page(() => import('@/games/slots/garden/GardenPage')),
 *
 * Slots live at /:lang/slots/:game, the rest at /:lang/<id>.
 */
type Page = LazyExoticComponent<ComponentType> | ComponentType;

const page = (load: () => Promise<{ default: ComponentType }>): Page => lazy(load);

/** Originals: /:lang/crash, /:lang/pot, /:lang/roulette, /:lang/carp, /:lang/firecrackers, /:lang/hongbao, /:lang/mahjong, /:lang/mahjong-table, /:lang/zhajinhua, /:lang/doudizhu */
export const GAME_PAGES: Record<Exclude<GameId, SlotId | SkillGameId>, Page> = {
  crash: page(() => import('@/games/crash/CrashPage')),
  pot: page(() => import('@/games/pot/PotPage')),
  roulette: page(() => import('@/games/roulette/RoulettePage')),
  carp: page(() => import('@/games/carp/CarpPage')),
  firecrackers: page(() => import('@/games/firecrackers/FirecrackersPage')),
  hongbao: page(() => import('@/games/hongbao/HongbaoPage')),
  mahjong: page(() => import('@/games/mahjong/MahjongPage')),
  'mahjong-table': page(() => import('@/games/mahjong-table/MahjongTablePage')),
  zhajinhua: page(() => import('@/games/zhajinhua/ZhajinhuaPage')),
  doudizhu: page(() => import('@/games/doudizhu/DoudizhuPage')),
};

/** Slots: /:lang/slots/<id> */
export const SLOT_PAGES: Record<SlotId, Page> = {
  garden: page(() => import('@/games/slots/garden/GardenPage')),
  deep: page(() => import('@/games/slots/deep/DeepPage')),
  spaceport: page(() => import('@/games/slots/spaceport/SpaceportPage')),
  'sweet-shop': page(() => import('@/games/slots/sweet-shop/SweetShopPage')),
  workshop: page(() => import('@/games/slots/workshop/WorkshopPage')),
  'koi-pond': page(() => import('@/games/slots/koi-pond/KoiPondPage')),
  'lucky-lever': page(() => import('@/games/slots/lucky-lever/LuckyLeverPage')),
  'neon-arcade': page(() => import('@/games/slots/neon-arcade/NeonArcadePage')),
  'pirate-cove': page(() => import('@/games/slots/pirate-cove/PirateCovePage')),
};

/**
 * Skill games: /:lang/puzzles/<id> and /:lang/strategy/chess (@tg/shared `gamePathOf`). Until a wave ships the game
 * (B: tile-match + color-sort, C: chess, D: daily-words) its line is the «coming soon» page; the wave replaces the
 * line with `page(() => import('@/games/<id>/<Name>Page'))` and drops `comingSoon` from GAME_CATALOG.
 */
export const SKILL_PAGES: Record<SkillGameId, Page> = {
  'tile-match': page(() => import('@/games/tile-match/TileMatchPage')),
  'color-sort': page(() => import('@/games/color-sort/ColorSortPage')),
  'daily-words': page(() => import('@/games/daily-words/DailyWordsPage')),
  chess: page(() => import('@/games/chess/ChessPage')),
};

/** System pages: /:lang/<path> */
export const SYSTEM_PAGES: Record<string, Page> = {
  profile: page(() => import('@/features/profile/ProfilePage')),
  passport: page(() => import('@/features/passport/PassportPage')),
  fair: page(() => import('@/features/fair/FairPage')),
  responsible: page(() => import('@/features/responsible/ResponsiblePage')),
  tournaments: page(() => import('@/features/social/TournamentsPage')),
  'tournaments/t/:id': page(() => import('@/features/tourney/TourneyPage')),
  friends: page(() => import('@/features/social/FriendsPage')),
  messages: page(() => import('@/features/chat/MessagesPage')),
  'messages/:publicId': page(() => import('@/features/chat/MessagesPage')),
  'i/:code': page(() => import('@/features/social/InvitePage')),
  'a/:id': page(() => import('@/features/share/AwardPage')),
  admin: page(() => import('@/features/admin/AdminPage')),
  privacy: page(() => import('@/features/privacy/PrivacyPage')),
  cookies: page(() => import('@/features/privacy/CookiesPage')),
  cooke: page(() => import('@/features/privacy/CookiesPage')),
  signin: page(() => import('@/features/auth/AuthPage')),
  signup: page(() => import('@/features/auth/AuthPage')),
  verify: page(() => import('@/features/auth/VerifyEmailPage')),
};

export const LOBBY_PAGE: Page = LobbyPage;
